import React, { useState, useEffect } from "react";
import { Flex, FormDialog, TextField, Typography, Checkbox, Box } from "flux";
import { BaseDialogProps } from "@adora/shared/contexts/dialog-context";
import useAdAssetGroup from "../../hooks/use-ad-asset-group";
import { AdAssetGroupData } from "../../types/ad-asset-group";
import { updateAdAssetGroup } from "../../features/ad-publishing/api";
import { useDialog } from "@adora/shared/hooks/use-dialog";
import AspectRatioCheckboxes from "@adora/shared/components/ad-asset-group-common/aspect-ratio-checkboxes";
import ErrorDialog from "../error-dialog";
import { ALL_PLATFORMS, Platform, PlatformEnum } from "@adora/shared/types/ad-platforms";
import { useBrandAdAccounts, BrandAdAccount } from "../../hooks/use-brand-ad-accounts";

interface UpdateAdAssetGroupDialogProps extends BaseDialogProps {
  brandId: number;
  adAssetGroupId: number;
}

const defaultFormData: AdAssetGroupData = {
  name: "",
  description: "",
  platforms: [],
  tracking_id_start: 1,
  google_ad_group_id: "",
  meta_ad_set_id: "",
  msan_ad_group_id: "",
  aspect_ratios: [],
  preferred_aspect_ratio_for_placements: "feed"
};

function validateUrl(field: string, value: string) {
  if (!value.toLowerCase().startsWith("http")) {
    return `${field} value must have a protocol (http or https)`;
  }
  return undefined;
}

const UpdateAdAssetGroupDialog: React.FC<UpdateAdAssetGroupDialogProps> = ({
  open,
  onClose,
  brandId,
  adAssetGroupId
}) => {
  const { data: adAccounts, isLoading: isLoadingAdAccounts } = useBrandAdAccounts(Number(brandId));
  const { adAssetGroup, error: adAssetGroupError } = useAdAssetGroup(adAssetGroupId, Number(brandId));
  const [formData, setFormData] = useState<AdAssetGroupData>(defaultFormData);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedAccounts, setSelectedAccounts] = useState<Record<string, string>>({});
  const { openDialog } = useDialog();

  useEffect(() => {
    if (adAssetGroup) {
      const currentPreference = adAssetGroup.preferred_aspect_ratio_for_placements;

      // Check if the adAssetGroup has a preferred value for "feed" OR if the value is "null"
      let preferredAspectRatio: "feed" | "square";
      if (currentPreference === "feed" || currentPreference === null) {
        // If so, default to checkbox checked (feed)
        preferredAspectRatio = "feed";
      } else {
        // If not, make it square
        preferredAspectRatio = "square";
      }

      setFormData((prev) => ({
        ...prev,
        name: adAssetGroup.name ?? prev.name,
        description: adAssetGroup.description ?? prev.description,
        platforms: adAssetGroup.platforms ?? prev.platforms,
        tracking_id_start: adAssetGroup.tracking_id_start ?? prev.tracking_id_start,
        google_ad_group_id: adAssetGroup.google_ad_group_id ?? prev.google_ad_group_id,
        meta_ad_set_id: adAssetGroup.meta_ad_set_id ?? prev.meta_ad_set_id,
        meta_ad_account_id: adAssetGroup.meta_ad_account_id ?? prev.meta_ad_account_id,
        msan_ad_group_id: adAssetGroup.msan_ad_group_id ?? prev.msan_ad_group_id,
        aspect_ratios: adAssetGroup.aspect_ratios ?? prev.aspect_ratios,
        preferred_aspect_ratio_for_placements: preferredAspectRatio
      }));
    }
  }, [adAssetGroup]);

  // Cross-reference ad asset group data with brand ad accounts to initialize selected accounts
  useEffect(() => {
    if (adAssetGroup && adAccounts) {
      const initialSelectedAccounts: Record<string, string> = {};

      // Get field values to check
      const googleAccountId =
        (adAssetGroup as unknown as Record<string, string>)["google_ad_account_id"] || adAssetGroup.google_ad_group_id;
      const metaAccountId =
        (adAssetGroup as unknown as Record<string, string>)["meta_ad_account_id"] || adAssetGroup.meta_ad_account_id;
      const msanAccountId =
        (adAssetGroup as unknown as Record<string, string>)["msan_ad_account_id"] || adAssetGroup.msan_ad_group_id;

      // For each platform, determine which account should be selected
      adAccounts.forEach((account) => {
        const platform = account.platform;

        if (platform === "google" && googleAccountId && !initialSelectedAccounts[platform]) {
          // For Google: if there's a value, select the first (and likely only) Google account
          initialSelectedAccounts[platform] = account.account_id;
        } else if (platform === "meta" && metaAccountId === account.account_id) {
          // For META: match specific account ID since there are multiple
          initialSelectedAccounts[platform] = account.account_id;
        } else if (platform === "msan" && msanAccountId && !initialSelectedAccounts[platform]) {
          // For MSAN: if there's a value, select the first (and likely only) MSAN account
          initialSelectedAccounts[platform] = account.account_id;
        } else if (
          platform === "pinterest" &&
          adAssetGroup.platforms?.includes("pinterest") &&
          !initialSelectedAccounts[platform]
        ) {
          initialSelectedAccounts[platform] = account.account_id;
        }
      });

      setSelectedAccounts(initialSelectedAccounts);
    }
  }, [adAssetGroup, adAccounts]);

  const handleInputChange = (field: keyof AdAssetGroupData, value: string | number | string[] | undefined) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const urlFields = ["google_ad_group_url", "meta_ad_set_url", "msan_ad_group_url"];
    const errors = urlFields
      .map((field) => {
        const data = formData[field as keyof AdAssetGroupData];
        if (data) return validateUrl(field, data as string);
        return undefined;
      })
      .filter(Boolean);

    if (errors.length) {
      await openDialog(ErrorDialog, {
        title: "Error",
        error: { errors: errors as string[], tag: "validationError", trace: "", message: "Validation error" }
      });
      return;
    }

    const updatedData: AdAssetGroupData = {
      name: formData.name,
      description: formData.description,
      platforms: formData.platforms,
      tracking_id_start: formData.tracking_id_start,
      google_ad_group_id: formData.google_ad_group_id,
      meta_ad_set_id: formData.meta_ad_set_id,
      meta_ad_account_id: formData.meta_ad_account_id,
      msan_ad_group_id: formData.msan_ad_group_id,
      aspect_ratios: formData.aspect_ratios,
      preferred_aspect_ratio_for_placements: formData.preferred_aspect_ratio_for_placements
    };

    setIsLoading(true);

    const result = await updateAdAssetGroup(adAssetGroupId, Number(brandId), updatedData);

    result.match(
      (error) => {
        openDialog(ErrorDialog, {
          title: "Error",
          error
        });
      },
      () => {
        onClose(true);
      }
    );

    setIsLoading(false);
  };

  const renderPlatformLabel = (adAccount: BrandAdAccount, platformIndex: number) => {
    const platform = adAccount.platform.toUpperCase();

    if (platform === "META") {
      return `META: Account ${platformIndex + 1} (${adAccount.account_id})`;
    }

    if (platform === "GOOGLE") {
      return "GOOGLE";
    }

    if (platform === "MSAN") {
      return "MSAN";
    }

    return `${platform}: ${adAccount.account_id}`;
  };

  const getGroupedAdAccounts = () => {
    if (!adAccounts) return [];

    // Group accounts by platform
    const grouped = adAccounts.reduce(
      (acc, account) => {
        const platform = account.platform;
        if (!acc[platform]) {
          acc[platform] = [];
        }
        acc[platform].push(account);
        return acc;
      },
      {} as Record<string, BrandAdAccount[]>
    );

    // Sort platforms in desired order and flatten with platform indices
    const platformOrder = ALL_PLATFORMS;
    const result: Array<{ account: BrandAdAccount; platformIndex: number }> = [];

    platformOrder.forEach((platform) => {
      if (grouped[platform]) {
        grouped[platform].forEach((account, index) => {
          result.push({ account, platformIndex: index });
        });
      }
    });

    return result;
  };

  const renderPlatformSpecificFields = () => {
    return (
      <>
        {formData.platforms?.includes(PlatformEnum.GDN) && (
          <>
            <TextField
              label="Google Ad Group ID"
              value={formData.google_ad_group_id || ""}
              onChange={(e) => handleInputChange("google_ad_group_id", e.target.value)}
              fullWidth
              placeholder="Optional"
              dataDogIdentifier="update ad asset group google ad group id"
            />
          </>
        )}

        {formData.platforms?.includes(PlatformEnum.META) && (
          <>
            <TextField
              label="Meta Ad Set ID"
              value={formData.meta_ad_set_id || ""}
              onChange={(e) => handleInputChange("meta_ad_set_id", e.target.value)}
              fullWidth
              placeholder="Optional"
              dataDogIdentifier="update ad asset group meta ad set id"
            />
          </>
        )}

        {formData.platforms?.includes(PlatformEnum.MSAN) && (
          <>
            <TextField
              label="MSAN Ad Group ID"
              value={formData.msan_ad_group_id || ""}
              onChange={(e) => handleInputChange("msan_ad_group_id", e.target.value)}
              fullWidth
              placeholder="Optional"
              dataDogIdentifier="update ad asset group msan ad group id"
            />
          </>
        )}
      </>
    );
  };

  if (adAssetGroupError) {
    return <Typography color="error">Error loading ad asset group data: {adAssetGroupError}</Typography>;
  }

  return (
    <FormDialog
      open={open}
      onClose={(e) => {
        if (window.confirm("Are you sure you want to close this dialog?")) {
          onClose(e);
        }
      }}
      title="Edit Group Details"
      primaryButtonText="Update"
      onSubmit={handleSubmit}
      isPrimaryButtonDisabled={isLoading}
      dataDogIdentifier="update ad asset group"
    >
      <Flex flexDirection="column" gap={2} mt="12px">
        <TextField
          label="Name"
          value={formData.name}
          onChange={(e) => handleInputChange("name", e.target.value)}
          fullWidth
          dataDogIdentifier="update ad asset group name"
        />
        <TextField
          label="Description"
          value={formData.description}
          onChange={(e) => handleInputChange("description", e.target.value)}
          fullWidth
          multiline
          rows={3}
          dataDogIdentifier="update ad asset group description"
        />

        <TextField
          label="Tracking ID Start"
          type="number"
          value={formData.tracking_id_start}
          onChange={(e) => {
            handleInputChange("tracking_id_start", Number.parseInt(e.target.value) || "");
          }}
          fullWidth
          dataDogIdentifier="update ad asset group tracking id start"
        />

        <Box>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Platforms
          </Typography>
          <Flex flexDirection="column" gap={1}>
            {!isLoadingAdAccounts &&
              getGroupedAdAccounts().map(({ account: adAccount, platformIndex }) => (
                <Box
                  key={adAccount.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    padding: "8px 0",
                    "&:hover": {
                      backgroundColor: "rgba(0, 0, 0, 0.02)"
                    }
                  }}
                >
                  <Checkbox
                    checked={selectedAccounts[adAccount.platform] === adAccount.account_id}
                    disabled={adAccount.platform === "pinterest"}
                    onChange={(e) => {
                      const platform = adAccount.platform as Platform;
                      let updatedPlatforms = [...(formData.platforms || [])];
                      const updatedSelectedAccounts = { ...selectedAccounts };

                      if (e.target.checked) {
                        // Add platform if not already present
                        if (!updatedPlatforms.includes(platform)) {
                          updatedPlatforms.push(platform);
                        }

                        // Track which specific account is selected for this platform
                        updatedSelectedAccounts[platform] = adAccount.account_id;

                        // Update the form data with the selected account ID for META
                        if (platform === "meta") {
                          handleInputChange("meta_ad_account_id", adAccount.account_id);
                        }
                      } else {
                        // Remove the platform
                        updatedPlatforms = updatedPlatforms.filter((p) => p !== platform);

                        // Remove the selected account tracking
                        delete updatedSelectedAccounts[platform];

                        // Clear the form data account ID for META when unchecked
                        if (platform === "meta") {
                          handleInputChange("meta_ad_account_id", "");
                        }
                      }

                      handleInputChange("platforms", updatedPlatforms);
                      setSelectedAccounts(updatedSelectedAccounts);
                    }}
                    dataDogIdentifier="update ad asset group platform"
                    sx={{
                      mr: 2,
                      "& .MuiSvgIcon-root": {
                        fontSize: 20
                      }
                    }}
                  />
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography>{renderPlatformLabel(adAccount, platformIndex)}</Typography>
                    {adAccount.platform === "pinterest" && (
                      <Typography variant="caption" color="text.secondary">
                        (Pinterest cannot be changed after AAG creation)
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
          </Flex>
        </Box>
        <AspectRatioCheckboxes
          value={formData.aspect_ratios}
          onChange={(aspectRatios) => handleInputChange("aspect_ratios", aspectRatios)}
          dataDogIdentifier="update ad asset group aspect ratio"
        />
        <Box>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Aspect Ratio Preferences
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              padding: "8px 0",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.02)"
              }
            }}
          >
            <Checkbox
              checked={formData.preferred_aspect_ratio_for_placements === "feed"}
              onChange={(e) => {
                handleInputChange("preferred_aspect_ratio_for_placements", e.target.checked ? "feed" : "square");
              }}
              dataDogIdentifier="update ad asset group preferred aspect ratio for placements"
            />
            <Typography>Use Feed (4:5) as preferred aspect ratio for feed placements</Typography>
          </Box>
        </Box>

        {renderPlatformSpecificFields()}
      </Flex>
    </FormDialog>
  );
};

export default UpdateAdAssetGroupDialog;
